import React from "react";
import Notes from "./Components/Notes.js";
import styled from "@emotion/styled";
import EditTags from "./Components/EditTags/EditTags.js";
import CancelIcon from '@mui/icons-material/Cancel';
import EditTargets from "./Components/EditTargets.js";
import ReportsModal from "./Components/ReportsModal.js";
import DismissLead from "./Components/LeadStatus/DismissLead.js";
import AddAssignees from "./Components/AddAssignees/AddAssignees.js";
import LeadTimeline from "../../Timeline/LeadTimeline";
import BeginClosing from "./Components/LeadStatus/BeginClosing";
import FinalizeLead from "./Components/LeadStatus/FinalizeLead.js";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ConfirmUpdate from "./Components/LeadStatus/ConfirmUpdate.js";
import CompareRecords from "./Components/CompareRecords.js";
import LeadActionsNav from "./LeadActionsNav";
import { useNavigate } from "react-router-dom";
import PropertyDetails from "./Components/PropertyDetails.js";
import InvestigateLead from "./Components/LeadStatus/InvestigateLead.js";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ProvideFinancials from "./Components/ProvideFinancials.js";
import ChangeTargetOutcome from "./Components/ChangeTargetOutcome.js";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { selectViewingLead, setViewingLead } from "../../../slices/viewingLeadSlice";
import { selectViewingReports, setViewingLeadReports } from "../../../slices/teamReportsSlice.js";
import { selectViewingMortgage, setViewingMortgage } from "../../../slices/viewingMortgageSlice";
import { selectTeamMembersByName } from "../../../slices/teamMembersSlice.js";
import { useDispatch, useSelector } from "react-redux";
import { removeNotificationFromMortgage, selectMortgagesFilteredBoolean, setFwdBckViewingFilteredMortgages, setFwdBckViewingMortgages } from "../../../slices/teamMortgagesSlice.js";
import { decrementMortgageNotificationsCount } from "../../../slices/userNotificationsSlice.js";
import { selectBackwardViewingLead, selectForwardViewingLead, setFwdBckFilteredViewingLeads, setFwdBckViewingLeads } from "../../../slices/teamActiveLeadsSlice.js";
import { ModalContainer } from "../../../StyledComponents";
import { selectLeadActionsNavSelection, setLeadStatusFilter, setMortgageActionNavMenuSelection, setNavSelection, setViewingLeadActions, setViewingMortgageActions } from "../../../slices/sessionDataSlice.js";

const LeadActionsModal = ({ handleCloseModal, userId, userFullName, dashPerspective, teamId, leadsFiltered }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()  

  const leadData = useSelector(selectViewingLead)
  const leadReports = useSelector(selectViewingReports)
  const mortgageData = useSelector(selectViewingMortgage)
  const navSelection = useSelector(selectLeadActionsNavSelection)
  const teamMembersByName = useSelector(selectTeamMembersByName)
  const forwardViewingLead = useSelector(selectForwardViewingLead)
  const backwardViewingLead = useSelector(selectBackwardViewingLead)
  const mortgagesFilteredBoolean = useSelector(selectMortgagesFilteredBoolean)

  const handleGoToMortgageActions = () => {
    if (mortgageData.notifCount && mortgageData.notifCount !== 0) {
      dispatch( decrementMortgageNotificationsCount(1) )
      dispatch( removeNotificationFromMortgage({mortgageId: mortgageData._id.toString()}) )
    }
    if (mortgagesFilteredBoolean) {
      dispatch( setFwdBckViewingFilteredMortgages(mortgageData._id) )
    } else {
      dispatch( setFwdBckViewingMortgages(mortgageData._id) )
    }
    dispatch( setNavSelection('repository') )
    dispatch( setMortgageActionNavMenuSelection('compareRecords') )
    navigate(`/dashboard/mortgageRepository/${mortgageData._id.toString()}`)
    dispatch( setViewingMortgageActions(true) )
    dispatch( setViewingLeadActions(false) )
  }

  const handleFwdBckViewingLead = (direction) => {
    if (direction === 'fwd') {
      dispatch( setViewingLead(forwardViewingLead) )
      dispatch( setViewingMortgage(forwardViewingLead.belongsToMortgage) )
      dispatch( setViewingLeadReports(forwardViewingLead.reports) )
      if (leadsFiltered || dashPerspective !== 'team') {
        dispatch( setFwdBckFilteredViewingLeads(forwardViewingLead._id) )
      } else {
        dispatch( setFwdBckViewingLeads(forwardViewingLead._id) )
      }
      navigate(`/dashboard/investigations/${forwardViewingLead._id}`)
      if (forwardViewingLead.status === 'awaitingAction') {
        dispatch( setLeadStatusFilter('Awaiting Action') )
      } else if (forwardViewingLead.status === 'investigating') {
        dispatch( setLeadStatusFilter('Investigating') )
      } else if (forwardViewingLead.status === 'closing') {
        dispatch( setLeadStatusFilter('Closing') )
      } else {
        dispatch( setLeadStatusFilter('Awaiting PR Updates') )
      }
    } else {
      dispatch( setViewingLead(backwardViewingLead) )
      dispatch( setViewingMortgage(backwardViewingLead.belongsToMortgage) )
      dispatch( setViewingLeadReports(backwardViewingLead.reports) )
      if (leadsFiltered || dashPerspective !== 'team') {
        dispatch( setFwdBckFilteredViewingLeads(backwardViewingLead._id) )
      } else {
        dispatch( setFwdBckViewingLeads(backwardViewingLead._id) )
      }
      navigate(`/dashboard/investigations/${backwardViewingLead._id}`)
      if (backwardViewingLead.status === 'awaitingAction') {
        dispatch( setLeadStatusFilter('Awaiting Action') )
      } else if (backwardViewingLead.status === 'investigating') {
        dispatch( setLeadStatusFilter('Investigating') )
      } else if (backwardViewingLead.status === 'closing') {
        dispatch( setLeadStatusFilter('Closing') )
      } else {
        dispatch( setLeadStatusFilter('Awaiting PR Updates') )
      }
    }
  }

  const getLeadStatusLabel = () => {
    if (leadData && leadData.status === 'awaitingAction') {
      return 'Potential Violation'
    }
    if (leadData && leadData.status === 'investigating') {
      const outcomeLabel = leadData.targetOutcome.toUpperCase() === 'RENEGOTIATION' ? 'Renegotiation' : 'Refinance'
      return `${outcomeLabel} Investigation`
    }
    if (leadData && leadData.status === 'awaitingUpdate') {
      return 'Awaiting Updates to Public Record'
    }
    return 'Closing Investigation'
  }

  return (
    <LeadModalShell className="threePanel">
      <ModalHeader>
        <RecordControls aria-label="Lead navigation">
          <RecordStepButton
            type="button"
            disabled={!backwardViewingLead}
            onClick={backwardViewingLead ? () => handleFwdBckViewingLead('bck') : undefined}
          >
            <ArrowBackIosIcon />
            prev
          </RecordStepButton>
          <RecordStepButton
            type="button"
            disabled={!forwardViewingLead}
            onClick={forwardViewingLead ? () => handleFwdBckViewingLead('fwd') : undefined}
          >
            next
            <ArrowForwardIosIcon />
          </RecordStepButton>
        </RecordControls>
        <InvestigationIdentity>
          <AddressTitle>{mortgageData.recordDetails.address.StreetAddressOneLine.currentValue}</AddressTitle>
          <StatusLabel $status={leadData?.status}>{getLeadStatusLabel()}</StatusLabel>
        </InvestigationIdentity>
        <HeaderActions>
          <RepositoryButton type="button" onClick={() => handleGoToMortgageActions()}>
            View in Mortgage Repository
            <OpenInNewIcon />
          </RepositoryButton>
          <CloseButton type="button" aria-label="Close Lead Actions Modal" onClick={() => handleCloseModal()}>
            <CancelIcon />
          </CloseButton>
        </HeaderActions>
      </ModalHeader>
      {leadData &&
        <>
          <LeadActionsNav mortgageData={mortgageData} leadOutcome={leadData.targetOutcome} reportsNumber={leadData.reports.length} leadStatus={leadData.status} handleCloseModal={handleCloseModal} leadAssigneesCount={leadData.assigneeIds.length} />
          <ContentPanel>
          {navSelection === 'reports' ?
            <ReportsModal reports={leadReports} />
          : navSelection === 'notes' ?
            <Notes userId={userId} userFullName={userFullName} leadData={leadData} mortgagestatus={mortgageData.status} />
          : navSelection === 'compareRecords' ?
            <CompareRecords mortgageData={mortgageData} userId={userId} userFullName={userFullName} leadData={leadData} teamId={teamId} location={'activeLead'} />
          : navSelection === 'propertyDetails' ?
            <PropertyDetails handleCloseModal={handleCloseModal} mortgageData={mortgageData} />
          : navSelection === 'editTargets' ?
            <EditTargets handleCloseModal={handleCloseModal} leadData={leadData} userId={userId} userFullName={userFullName} dashPerspective={dashPerspective} mortgageData={mortgageData} />
          : navSelection === 'editTags' ?
            <EditTags leadData={leadData} userFullName={userFullName} teamId={teamId} dashPerspective={dashPerspective} userId={userId} activeLead={mortgageData.activeLead} mortgageData={mortgageData} />
          : navSelection === 'addAssignees' ?
            <AddAssignees  teamMembersByName={teamMembersByName} handleCloseModal={handleCloseModal} leadData={leadData} userId={userId} userFullName={userFullName} activeLead={mortgageData.activeLead} mortgageData={mortgageData} />
          : navSelection === 'investigateLead' ?
            <InvestigateLead handleCloseModal={handleCloseModal} userId={userId} teamId={teamId} userFullName={userFullName} teamMembersByName={teamMembersByName} mortgageId={mortgageData._id} leadData={leadData} dashPerspective={dashPerspective} requestOrigin={'activeLeads'} />
          : navSelection === 'dismissLead' ?
            <DismissLead handleCloseModal={handleCloseModal} userFullName={userFullName} teamId={teamId} userId={userId} dashPerspective={dashPerspective} leadData={leadData} />
          : navSelection === 'targetOutcome' ?
            <ChangeTargetOutcome handleCloseModal={handleCloseModal} leadData={leadData} userId={userId} userFullName={userFullName} />
          : navSelection === 'beginClosingLead' ?
            <BeginClosing handleCloseModal={handleCloseModal} leadData={leadData} userId={userId} userFullName={userFullName} teamId={teamId} />
          : navSelection === 'finalizeLead' ?
            <FinalizeLead handleCloseModal={handleCloseModal} leadData={leadData} userId={userId} userFullName={userFullName} teamId={teamId} mortgageData={mortgageData} />
          : navSelection === 'confirmUpdate' ?
            <ConfirmUpdate handleCloseModal={handleCloseModal} leadData={leadData} userId={userId} userFullName={userFullName} teamId={teamId} mortgageData={mortgageData} />
          : navSelection === 'provideFinancialsModal' || navSelection === 'provideFinancialsCard' || navSelection === 'provideTerms' ?
            <ProvideFinancials mortgageData={mortgageData} userFullName={userFullName} leadData={leadData} userId={userId} navSelection={navSelection} handleCloseModal={handleCloseModal} teamId={teamId} />
          :
            <></>
          }
          </ContentPanel>
        </>
      }
      <TimelinePanel>
        <TimelineHeader>Timeline</TimelineHeader>
        <TimelineBody>
        {leadData &&
          <LeadTimeline origin={'modal'} />
        }
        </TimelineBody>
      </TimelinePanel>
    </LeadModalShell>
  )
}

const LeadModalShell = styled(ModalContainer)(({ theme }) => `
  &.threePanel {
    display: grid;
    grid-template-columns: 22rem minmax(0, 1fr) 34rem;
    grid-template-rows: auto minmax(0, 1fr);
    gap: 1rem;
    height: min(66rem, calc(100vh - 4rem));
    max-height: calc(100vh - 4rem);
    padding: 1rem;
    background: ${theme.palette.common.pageBackground};
    overflow: hidden;
  }

  .leadActions {
    grid-column: auto;
    grid-row: auto;
    min-height: 0;
    max-height: 100%;
  }
`)

const ModalHeader = styled.header(({ theme }) => `
  grid-column: 1 / 4;
  grid-row: 1;
  min-height: 6.4rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 1rem;
  background: ${theme.palette.common.cardBackground};
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  box-shadow: 0 0.4rem 1.4rem rgba(15, 23, 42, 0.08);
`)

const RecordControls = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const RecordStepButton = styled.button(({ theme }) => `
  min-width: 5.6rem;
  height: 3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  padding: 0 0.7rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.6rem;
  background: ${theme.palette.common.white};
  color: ${theme.palette.primary.main};
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    background: ${theme.palette.common.grayBorderLight};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${theme.palette.common.grayText};
    background: ${theme.palette.common.grayBorderLight};
    opacity: 0.65;
  }
`)

const InvestigationIdentity = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const AddressTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1.8rem;
  font-weight: 750;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`)

const StatusLabel = styled.p(({ theme, $status }) => {
  const statusColor = $status === 'awaitingAction'
    ? theme.palette.common.redMediumTransparent
    : $status === 'investigating'
      ? theme.accent.caution.primary
      : theme.accent.success.primary

  return `
    margin: 0;
    color: ${statusColor};
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
  `
})

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
`

const ContentPanel = styled.main(({ theme }) => `
  grid-column: 2;
  grid-row: 2;
  height: 100%;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  overflow: hidden;
  background: ${theme.palette.common.cardBackground};
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;

  > * {
    height: 100%;
    width: 100%;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
  }
`)

const RepositoryButton = styled.button(({ theme }) => `
  height: 3.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 1rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.6rem;
  background: ${theme.palette.common.white};
  color: ${theme.palette.primary.main};
  font-size: 0.95rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    background: ${theme.palette.common.grayBorderLight};
  }
`)

const CloseButton = styled.button(({ theme }) => `
  width: 3.2rem;
  height: 3.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 50%;
  background: ${theme.palette.common.white};
  color: ${theme.palette.common.grayText};
  cursor: pointer;

  svg {
    width: 1.7rem;
    height: 1.7rem;
  }

  &:hover {
    color: ${theme.palette.common.black};
    background: ${theme.palette.common.grayBorderLight};
  }
`)

const TimelinePanel = styled.aside(({ theme }) => `
  grid-column: 3;
  grid-row: 2;
  height: 100%;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: ${theme.palette.common.cardBackground};
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  overflow: hidden;
`)

const TimelineHeader = styled.div(({ theme }) => `
  flex: 0 0 auto;
  padding: 1rem 1.2rem;
  color: ${theme.palette.common.black};
  font-size: 1.1rem;
  font-weight: 750;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const TimelineBody = styled.div(({ theme }) => `
  min-height: 0;
  height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  align-self: stretch;
  overflow-x: hidden;
  overflow-y: hidden;
  background: ${theme.palette.common.grayBorder};

  > * {
    min-height: 0;
    height: 100%;
    max-height: 100%;
  }
`)

export default LeadActionsModal;
